import { defineStyle, defineStyleConfig, extendTheme } from '@chakra-ui/react'
import {
  tabsAnatomy as tabsParts,
  modalAnatomy as modalParts,
  checkboxAnatomy,
  sliderAnatomy,
} from '@chakra-ui/anatomy'
import { createMultiStyleConfigHelpers } from '@chakra-ui/styled-system'

const { defineMultiStyleConfig: defineMultiStyleModalConfig } = createMultiStyleConfigHelpers(modalParts.keys)

const { defineMultiStyleConfig: defineMultiStyleTabsConfig } = createMultiStyleConfigHelpers(tabsParts.keys)

const { defineMultiStyleConfig: defineMultiStyleCheckBoxConfig } = createMultiStyleConfigHelpers(checkboxAnatomy.keys)

const { defineMultiStyleConfig: defineMultiStyleSliderConfig } = createMultiStyleConfigHelpers(sliderAnatomy.keys)

export const colors = {
  brand: {
    white: '#FFFFFF',
    link: '#A8A3FF',
    purple: '#5048A3',
    indygo: '#5D52EE',
    green: '#68F276',
    error: '#FF7D7D',
    yellow: '#FFA723',
  },
  ninja: {
    100: '#DCD9FF',
    200: '#8784B7',
    300: '#6966A4',
    400: '#434172',
    500: '#282759',
    600: '#1D1C49',
    700: '#171741',
    800: '#15143D',
    900: '#121236',
    1000: '#13133A',
  },
  gradient: {
    primary: `linear(to-r, #8E83FB, #5D52EE)`,
    secondary: `linear(to-r, #7163FF, #5D52EE)`,
    orange: `linear(to-r, #FF7C1D, #FF9737)`,
    highlight: `linear(to-r, #1D1C49, #323179)`,
  },
}

const tabsTheme = defineMultiStyleTabsConfig({
  baseStyle: {
    tab: {
      borderBottom: '0px solid',
      borderColor: 'transparent',
      color: 'ninja.300',
      fontSize: 'xl',
      padding: '16px',
      paddingTop: '16px',
      fontWeight: 'bold',
      borderRadius: 'xl',
      _hover: {
        color: 'brand.white',

        background: 'ninja.900',
      },
      _selected: {
        color: 'brand.white',
        background: 'ninja.900',
      },
    },
    tablist: {
      borderRadius: 'xl',
      padding: '4px',
      background: 'ninja.500',
      borderBottom: '0px solid',
      borderColor: 'inherit',
    },
    tabpanel: {},
  },
  sizes: {
    xl: {
      tab: {
        fontSize: 'sm',
      },
    },
  },
})

const checkboxTheme = defineMultiStyleCheckBoxConfig({
  baseStyle: {
    control: defineStyle({
      borderColor: 'ninja.300',
      border: '1px',
      _hover: {
        borderColor: 'brand.link',
      },
      _checked: {
        borderColor: 'ninja.300',
        bgGradient: colors.gradient.primary,
        _hover: {
          borderColor: 'brand.link',
          bgGradient: colors.gradient.primary,
        },
      },
    }),
  },
  defaultProps: {
    size: 'xl',
  },
  sizes: {
    xl: {
      icon: {
        height: '0.625rem',
        width: '0.625rem',
      },
      control: {
        height: '1.5rem',
        width: '1.5rem',
        rounded: '8px',
      },
    },
  },
})

const buttonTheme = defineStyleConfig({
  baseStyle: {
    fontWeight: 'bold',
    transition: '.2s',
  },
  sizes: {
    lg: {
      fontSize: 'md',
      borderRadius: 'xl',
      px: 6,
      h: '56px',
    },
    md: {
      borderRadius: 'full',
      fontSize: 'sm',
      px: 6,
      h: 46,
    },
    sm: {
      fontSize: 'sm',
      rounded: 'lg',
      px: 2,
      h: 30,
    },
  },
  variants: {
    orange: {
      bg: '#ED8516',
      bgGradient: 'linear(to-r, #FF7C1D, #FF9737)',
      _hover: {
        filter: `drop-shadow(0 4px 25px #ED851652)`,
        bgGradient: 'linear(to-r, #FF7C1D, #FF9737)',
        _disabled: {
          bgGradient: 'linear(to-r, #FF7C1D, #FF9737)',
        },
      },
      _active: {
        bgGradient: 'linear(to-r, #FF7C1D, #FF9737)',
      },
    },
    secondary: {
      bg: colors.ninja[500],
      color: 'white',
      _hover: {
        filter: `drop-shadow(0 4px 25px rgba(110, 97, 253, .51))`,
        bg: colors.ninja[400],
      },
      _disabled: {
        bg: colors.ninja[500],
      },
    },
    solid: {
      bg: 'ninja.500',
      bgGradient: colors.gradient.primary,
      color: 'white',
      borderColor: '#5D52EE',
      borderWidth: '1px',
      _hover: {
        filter: `drop-shadow(0 4px 25px rgba(110, 97, 253, .51))`,
        bgGradient: colors.gradient.secondary,
        color: 'white',
        _disabled: {
          bgGradient: colors.gradient.primary,
        },
      },
      _active: {
        bgGradient: colors.gradient.primary,
      },
    },
    outline: {
      color: 'ninja.300',
      borderColor: 'ninja.400',
      // borderWidth: '1px',
      bg: 'ninja.500',
      _hover: {
        bgGradient: colors.gradient.secondary,
        bg: 'ninja.500',
        filter: `drop-shadow(0 4px 25px rgba(110, 97, 253, .51))`,
        color: 'white',
      },
      _active: {
        bgGradient: colors.gradient.primary,
      },
    },
  },
  defaultProps: {
    size: 'md',
    variant: 'solid',
  },
})

export const dividerTheme = defineStyleConfig({
  baseStyle: {
    borderColor: 'ninja.400',
  },
})

export const headingTheme = defineStyleConfig({
  sizes: {
    lg: {
      fontSize: '2rem',
    },
  },
})

export const closeButtonTheme = defineStyleConfig({
  baseStyle: {
    bg: `rgb(135, 132, 183, 0.2)`, // nininja 200
    color: '#8784B7',
    borderRadius: '1.25rem',
    _hover: {
      bg: `rgb(135, 132, 183, 0.4)`, // nininja 300
    },
  },
})

export const modalTheme = defineMultiStyleModalConfig({
  baseStyle: {
    dialog: {
      borderRadius: '1.25rem',
      bg: `ninja.600`,
    },
  },
})

export const sliderTheme = defineMultiStyleSliderConfig({
  baseStyle: {
    thumb: {
      border: 0,
      bg: 'brand.indygo', // change the background of the thumb to orange.400
    },
    track: {
      bg: 'ninja.500',
    },
    filledTrack: {
      bgImage: colors.gradient.primary, // change the background of the filled track to blue.600
    },
  },
})

export default extendTheme({
  styles: {
    global: {
      body: {
        bg: '#13133A',
        color: 'brand.white',
      },
    },
  },
  fonts: {
    body: `'Wix Madefor Text Variable', sans-serif;`,
    heading: `'Wix Madefor Text Variable', sans-serif;`,
    mono: 'Poppins',
  },
  semanticTokens: {
    colors: {
      'chakra-body-text': { _light: 'brand.white' },
      'chakra-placeholder-color': { _light: 'brand.white' },
    },
  },
  components: {
    Modal: modalTheme,
    CloseButton: closeButtonTheme,
    Button: buttonTheme,
    Heading: headingTheme,
    Divider: dividerTheme,
    Tabs: tabsTheme,
    Checkbox: checkboxTheme,
    Slider: sliderTheme,
  },
  colors,
})
